package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBugsThatDontFailPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/BugsThatDontFail.md", FrontMatterElement.Builder { addScalar("title", "The bugs that don't fail"); addScalar("searchTitle", "MongoDB driver for Kotlin/Native — four bugs that never threw"); addScalar("date", "2026-08-07"); addScalar("description", "294 tests, 71 specification scenarios, and four bugs that only a real mongod would show — because none of them threw anything."); addScalar("library", "mongkn"); }))
}

@Page("/blog/bugs-that-dont-fail")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun BugsThatDontFailPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mongkn") {
            org.jetbrains.compose.web.dom.Text("mongkn")
        }
        org.jetbrains.compose.web.dom.Text(" is a MongoDB driver for Kotlin/Native. By the time I moved a real service onto it, it had 294 tests and passed 71 scenarios from MongoDB's own specifications.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Four bugs were waiting anyway. Not one of them threw. Every one returned a plausible answer that happened to be wrong.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-most-expensive-four-lines") }) {
        org.jetbrains.compose.web.dom.Text("The most expensive four lines")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The query was ordinary:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""collection.find { Landing::shopId eq shopId }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("shopId")
        }
        org.jetbrains.compose.web.dom.Text(" is a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("String")
        }
        org.jetbrains.compose.web.dom.Text(" in Kotlin and an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ObjectId")
        }
        org.jetbrains.compose.web.dom.Text(" in the database — a custom serializer converts it on the way in. But the filter builder encoded by the value's ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("runtime type")
        }
        org.jetbrains.compose.web.dom.Text(", not by the field's serializer. So the filter carried a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BsonString")
        }
        org.jetbrains.compose.web.dom.Text(" and the stored field was a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BsonObjectId")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Mongo compared them, found nothing, and said so. Empty result, no error, no warning. The query was valid; it just asked about a field that never has that type.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The fix is to encode a filter value through the serializer of the field it is compared against, which needs ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("childSerializers()")
        }
        org.jetbrains.compose.web.dom.Text(" on the generated serializer. Four lines. Days to find, because nothing anywhere was red.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-more-of-the-same-shape") }) {
        org.jetbrains.compose.web.dom.Text("Three more of the same shape")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A missing ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("_id")
                }
                org.jetbrains.compose.web.dom.Text(" mapping.")
            }
            org.jetbrains.compose.web.dom.Text(" Without ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("@SerialName(\"_id\")")
            }
            org.jetbrains.compose.web.dom.Text(" the class writes an ordinary ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("id")
            }
            org.jetbrains.compose.web.dom.Text(" field and Mongo generates its own ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("_id")
            }
            org.jetbrains.compose.web.dom.Text(" beside it. Two identifiers, one document, and lookups by the one you care about quietly miss.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A sparse index that indexed everything.")
            }
            org.jetbrains.compose.web.dom.Text(" An explicit ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("null")
            }
            org.jetbrains.compose.web.dom.Text(" is written as ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("BsonNull")
            }
            org.jetbrains.compose.web.dom.Text(", which is a present value. Sparse means \"skip documents without the key\", and every document had the key. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("@EncodeDefault(NEVER)")
            }
            org.jetbrains.compose.web.dom.Text(" is what makes absent mean absent.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A TTL index that stopped deleting.")
            }
            org.jetbrains.compose.web.dom.Text(" Timestamps stored as ISO strings rather than BSON dates. The index exists, it is valid, and it never expires a thing.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-defence-that-was-already-there") }) {
        org.jetbrains.compose.web.dom.Text("The defence that was already there")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The library already refused one class of mistake. A property reference in a filter is checked against the class descriptor, so a renamed field fails immediately with a message that says which one. That check works — nobody had ever been bitten by it, which is the point.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the fix was not to invent a new mechanism. It was to extend the one that already earned its place: validate the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("value")
        }
        org.jetbrains.compose.web.dom.Text(" the way the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("field")
        }
        org.jetbrains.compose.web.dom.Text(" was already validated.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A rule went into the test docs at the same time:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Native storage layers are verified by integration tests against a real ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("mongod")
            }
            org.jetbrains.compose.web.dom.Text(", not by unit tests. Everything here that could break broke silently, and only a real server showed it.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-ladder-is-an-upper-bound-not-a-promise") }) {
        org.jetbrains.compose.web.dom.Text("A ladder is an upper bound, not a promise")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("While measuring, I built a five-level performance ladder to see what each layer costs. The coroutine channel came out at 0.22 µs per document. Batching 64 documents across it was predicted to save that 0.22 µs and actually saved 0.47 µs on macOS and 0.30 on Linux — the extra came from context switches that disappeared with the crossings.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then a decoding optimisation predicted to save 0.43 µs delivered ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("4 %")
        }
        org.jetbrains.compose.web.dom.Text(" on the real read path, despite being 37 % faster in isolation.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is the lesson, and it generalises past this project:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("The cost of a layer in a ladder is an upper bound on the win, not the win. A layer that is not on the critical path gives you nothing when you delete it.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-baseline-that-moved") }) {
        org.jetbrains.compose.web.dom.Text("The baseline that moved")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A bare C loop — the floor, the thing that cannot get faster — measured 6667 µs in one run and 10710 µs in another on the same machine. A 1.5× swing in the control.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Comparing numbers across runs was meaningless, and I had been doing exactly that. Interleaving the rounds instead of finishing one level before starting the next made the results reproducible, because every level then absorbed the same machine noise.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-check-that-was-not-running") }) {
        org.jetbrains.compose.web.dom.Text("The check that was not running")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A test had been failing on one platform for weeks. It was known, it was tolerated — and because it failed, the ABI validation task never ran. The public API drifted for weeks behind a red light everyone had learned to read as normal.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two changes: the reference dump moved to the platform that actually ships (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text("), and the red test was closed by asserting a weaker property that holds on both branches.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-i-would-tell-myself-a-year-earlier") }) {
        org.jetbrains.compose.web.dom.Text("What I would tell myself a year earlier")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Look for the ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("shape")
            }
            org.jetbrains.compose.web.dom.Text(" — a wrong answer with no exception — before it costs you data.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Extend a defence that already works rather than adding a new kind.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Move a real service onto it early. Specification scenarios do not cover the four bugs above, because none of them are protocol violations.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Verify measurements under the conditions you will actually run in.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/mongkn") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/mongkn")
        }
    }
}
